import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import { DateRange as DateRangeIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, subDays, subMonths, startOfWeek, startOfMonth, startOfYear } from 'date-fns';

interface DateRangeSelectorProps {
  fromDate: string;
  toDate: string;
  onChange: (dateRange: { fromDate: string; toDate: string }) => void;
}

const datePresets = [
  { label: 'Today', value: 1 },
  { label: 'Yesterday', value: 1, offset: 1 },
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'This week', start: () => startOfWeek(new Date()), end: () => new Date() },
  { label: 'This month', start: () => startOfMonth(new Date()), end: () => new Date() },
  { label: 'Last month', start: () => startOfMonth(subMonths(new Date(), 1)), end: () => subDays(startOfMonth(new Date()), 1) },
  { label: 'This year', start: () => startOfYear(new Date()), end: () => new Date() },
  { label: 'Last 90 days', value: 90 },
  { label: 'Last 365 days', value: 365 },
];

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ fromDate, toDate, onChange }) => {
  const theme = useTheme();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [startDate, setStartDate] = useState<Date | null>(
    fromDate ? new Date(fromDate) : subDays(new Date(), 30)
  );
  const [endDate, setEndDate] = useState<Date | null>(
    toDate ? new Date(toDate) : new Date()
  );
  
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handlePresetClick = (preset: typeof datePresets[0]) => {
    let newStartDate: Date;
    let newEndDate: Date = new Date();
    
    if (preset.start && preset.end) {
      newStartDate = preset.start();
      newEndDate = preset.end();
    } else {
      const days = preset.value;
      const offset = preset.offset || 0;
      
      if (offset) {
        newEndDate = subDays(new Date(), offset);
      }
      
      newStartDate = subDays(newEndDate, days - 1);
    }
    
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    
    onChange({
      fromDate: format(newStartDate, 'yyyy-MM-dd'),
      toDate: format(newEndDate, 'yyyy-MM-dd'),
    });
    
    handleClose();
  };
  
  const handleApply = () => {
    if (startDate && endDate) {
      onChange({
        fromDate: format(startDate, 'yyyy-MM-dd'),
        toDate: format(endDate, 'yyyy-MM-dd'),
      });
    }
    
    handleClose();
  };
  
  const getDateRangeLabel = () => {
    if (fromDate && toDate) {
      const startDate = new Date(fromDate);
      const endDate = new Date(toDate);
      
      // Check if it's the same year
      if (startDate.getFullYear() === endDate.getFullYear()) {
        return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
      } else {
        return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
      }
    }
    
    return 'Select dates';
  };
  
  return (
    <Box sx={{ mr: 2 }}>
      <Button
        variant="outlined"
        onClick={handleClick}
        startIcon={<DateRangeIcon />}
        size="small"
        sx={{
          color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary,
          borderColor: theme.palette.divider,
        }}
      >
        {getDateRangeLabel()}
      </Button>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{ '& .MuiMenu-paper': { width: 320, maxWidth: '100%' } }}
      >
        <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>
          Select Date Range
        </Typography>
        
        <Box sx={{ display: 'flex', p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(date) => setStartDate(date)}
              slotProps={{ textField: { size: 'small', sx: { mr: 1 } } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(date) => setEndDate(date)}
              minDate={startDate || undefined}
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>
        </Box>
        
        <Divider />
        
        <List dense sx={{ pt: 0, pb: 0 }}>
          {datePresets.map((preset) => (
            <ListItem button key={preset.label} onClick={() => handlePresetClick(preset)}>
              <ListItemText primary={preset.label} />
            </ListItem>
          ))}
        </List>
        
        <Divider />
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <Button onClick={handleClose} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply} color="primary">
            Apply
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default DateRangeSelector;